import { cn } from '@amore/ui/cn'
import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Input } from '@amore/ui/components/input'
import { ChevronDown, Search, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'
import { plural } from '@/lib/format'
import { categoryLabel } from './model'

type Change = { category?: string; region?: string; q?: string }

/**
 * The catalog's filters: search, a "Filtry" button that opens a panel with
 * category and region chips, and the number of results. Active filters stay
 * visible as removable pills when the panel is closed. Many categories don't
 * fit one chip row, so they wait behind the button.
 */
export function CatalogFilters({
  categories,
  regions,
  category,
  region,
  query,
  count,
  onChange,
}: {
  categories: string[]
  regions: string[]
  category: string
  region: string
  query: string
  count: number
  onChange: (change: Change) => void
}) {
  const [open, setOpen] = useState(false)
  const active = [
    category !== 'all' && {
      key: 'category',
      label: categoryLabel(category),
      clear: () => onChange({ category: 'all' }),
    },
    region !== 'all' && {
      key: 'region',
      label: region,
      clear: () => onChange({ region: 'all' }),
    },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-80">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            value={query}
            onChange={(event) => onChange({ q: event.target.value })}
            placeholder="Hledat v katalogu"
            aria-label="Hledat v katalogu"
            className="pl-10"
          />
        </div>
        <Button
          appearance="outline"
          tone="neutral"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={cn(active.length > 0 && 'border-action')}
        >
          <SlidersHorizontal aria-hidden="true" className="size-4" />
          Filtry
          {active.length > 0 ? (
            <span className="grid h-5 min-w-5 place-items-center rounded-full bg-action px-1.5 text-[11px] leading-none text-action-foreground tabular-nums">
              {active.length}
            </span>
          ) : null}
          <ChevronDown
            aria-hidden="true"
            className={cn('size-4 transition-transform', open && 'rotate-180')}
          />
        </Button>
        {!open && active.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            {active.map((item) => (
              <Badge
                key={item.key}
                tone="accent"
                removeLabel={`Zrušit filtr ${item.label}`}
                onRemove={item.clear}
              >
                {item.label}
              </Badge>
            ))}
          </div>
        ) : null}
        <span className="flex-1" />
        <span className="text-sm text-muted-foreground tabular-nums">
          {count} {plural(count, 'dodavatel', 'dodavatelé', 'dodavatelů')}
        </span>
      </div>

      {open ? (
        <div className="space-y-5 rounded-panel border border-border bg-card p-5 sm:p-6">
          <ChipGroup
            label="Kategorie"
            options={[
              { value: 'all', label: 'Vše' },
              ...categories.map((value) => ({ value, label: categoryLabel(value) })),
            ]}
            value={category}
            onChange={(value) => onChange({ category: value })}
          />
          <ChipGroup
            label="Kraj"
            options={[
              { value: 'all', label: 'Celá ČR' },
              ...regions.map((value) => ({ value, label: value })),
            ]}
            value={region}
            onChange={(value) => onChange({ region: value })}
          />
          {active.length > 0 ? (
            <div className="border-t border-border pt-4">
              <Button
                appearance="ghost"
                tone="neutral"
                size="sm"
                onClick={() => onChange({ category: 'all', region: 'all' })}
              >
                Zrušit filtry
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function ChipGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { value: string; label: string }[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <fieldset className="space-y-2.5">
      <legend className="text-sm font-semibold">{label}</legend>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = option.value === value
          return (
            // biome-ignore lint/a11y/useSemanticElements: chip styling; radio semantics via role
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(option.value)}
              className={cn(
                'inline-flex min-h-9 items-center rounded-full border px-4 text-xs font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                selected
                  ? 'border-action bg-accent text-accent-foreground'
                  : 'border-border bg-card text-foreground/75 hover:bg-muted',
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}
